/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import { useState } from 'react'

import { Button, Icon } from 'semantic-ui-react'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'

import { notify } from 'react-notify-toast'
import { useTranslation } from 'react-i18next'
import { updateOrderCouriers } from '../../../services/Order/Order.service'
import { MOBILE_THRESHOLD } from '../../../constants'
import { useMutation } from '@tanstack/react-query'

const RequestCourier = ({order, afterSubmit}) => {
    const [t] = useTranslation()
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD}px)`)
    const [couriers, setCouriers] = useState(order.couriers || 1)

    const updateOrderCouriersMutation = useMutation({
        mutationFn: () => updateOrderCouriers(order, couriers),
        onError: (error) => {
            if (error.message === 'UnsupportedOperationError') {
                notify.show(t('order.couriers.unsupported'), 'error')
            } else {
                notify.show(t('global.anErrorOccurred'), 'error')
            }
        },
        onSettled: () => {
            afterSubmit()
        }
    })

    const courierOptions = [
        { value: 1, bags: '1-3', icon: 1 },
        { value: 2, bags: '4-6', icon: 2 },
        { value: 3, bags: '7-9', icon: 3 },
        { value: 4, bags: '10+', icon: 4 }
    ]

    return (
      <div css={css`
          padding: 0;
          max-width: 600px;
          margin: 0 auto;
      `}>
          <div css={css`
              display: grid;
              grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'};
              gap: 8px;
              margin-bottom: 12px;
          `}>
              {courierOptions.map(option => {
                  const isSelected = couriers === option.value
                  const isDisabled = updateOrderCouriersMutation.isPending || order.couriers > option.value

                  return (
                      <div
                          key={option.value}
                          onClick={() => !isDisabled && setCouriers(option.value)}
                          css={css`
                              background: ${isSelected ? '#00b5ad' : '#fff'};
                              border: 2px solid ${isSelected ? '#00b5ad' : '#ddd'};
                              border-radius: 6px;
                              padding: ${isMobile ? '12px 10px' : '14px 12px'};
                              cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
                              opacity: ${isDisabled ? 0.5 : 1};
                              transition: all 0.2s ease;
                              text-align: center;
                              
                              &:hover {
                                  ${!isDisabled && !isSelected && 'border-color: #00b5ad; box-shadow: 0 2px 8px rgba(0,181,173,0.15);'}
                              }
                              
                              &:active {
                                  ${!isDisabled && 'transform: scale(0.98);'}
                              }
                          `}
                      >
                          <div css={css`
                              display: flex;
                              justify-content: center;
                              gap: 4px;
                              margin-bottom: 8px;
                              font-size: ${isMobile ? '18px' : '20px'};
                          `}>
                              {Array.from({ length: option.icon }).map((_, i) => (
                                  <Icon
                                      key={i}
                                      name="user"
                                      css={css`color: ${isSelected ? '#fff' : '#00b5ad'} !important;`}
                                  />
                              ))}
                          </div>
                          <div css={css`
                              font-size: ${isMobile ? '14px' : '15px'};
                              font-weight: 600;
                              color: ${isSelected ? '#fff' : '#333'};
                              margin-bottom: 2px;
                          `}>
                              {option.icon} {option.icon === 1 ? t('order.couriers.courier') : t('order.couriers.couriers')}
                          </div>
                          <div css={css`
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              gap: 3px;
                              font-size: ${isMobile ? '12px' : '13px'};
                              color: ${isSelected ? 'rgba(255,255,255,0.9)' : '#666'};
                          `}>
                              <Icon
                                  name="shopping bag"
                                  size="small"
                                  css={css`
                                      margin: 0 !important;
                                      color: ${isSelected ? 'rgba(255,255,255,0.9)' : '#666'} !important;
                                  `}
                              />
                              {option.bags} {t('order.couriers.bags')}
                          </div>
                      </div>
                  )
              })}
          </div>

          <div css={css`
              text-align: ${isMobile ? 'center' : 'right'};
          `}>
              <Button
                color="teal"
                size={isMobile ? 'medium' : 'large'}
                fluid={isMobile}
                loading={updateOrderCouriersMutation.isPending}
                disabled={updateOrderCouriersMutation.isPending || couriers === order.couriers}
                onClick={updateOrderCouriersMutation.mutate}
                css={css`
                    min-width: ${isMobile ? 'auto' : '180px'};
                `}
              >
                  <Icon name="checkmark"/>
                  {t('order.couriers.validate')}
              </Button>
          </div>
      </div>
    )
}

export default RequestCourier
